import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './AnalyticsDashboardPage.css';

interface DashboardStats {
  totalTestCases: number;
  totalScenarios: number;
  totalProjects: number;
  totalRuns: number;
  completedRuns: number;
  passCount: number;
  failCount: number;
  blockedCount: number;
  successRate: number;
  executionTrend: { date: string; pass: number; fail: number; blocked: number }[];
}

interface ActivityRow {
  sort_key: string;
  period: string;
  user: string;
  // Overall / By User view
  tc_created?: number;
  tc_executed?: number;
  sc_created?: number;
  sc_executed?: number;
  runs_created?: number;
  runs_completed?: number;
  // AI vs Manual view
  tc_ai?: number;
  tc_manual?: number;
  tc_ai_pct?: number;
  tc_manual_pct?: number;
  sc_ai?: number;
  sc_manual?: number;
  sc_ai_pct?: number;
  sc_manual_pct?: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#ef4444',
  High: '#f97316',
  Medium: '#3b82f6',
  Low: '#9ca3af',
};

export const AnalyticsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [stats, setStats] = useState<DashboardStats>({
    totalTestCases: 0, totalScenarios: 0, totalProjects: 0,
    totalRuns: 0, completedRuns: 0,
    passCount: 0, failCount: 0, blockedCount: 0, successRate: 0,
    executionTrend: [],
  });
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Activity report state
  const [actPeriod, setActPeriod] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [actView, setActView] = useState<'overall' | 'by_user' | 'ai_vs_manual'>('overall');
  const [actMonths, setActMonths] = useState(12);
  const [actRows, setActRows] = useState<ActivityRow[]>([]);
  const [actUsers, setActUsers] = useState<string[]>([]);
  const [actUserFilter, setActUserFilter] = useState<string>('');
  const [actLoading, setActLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Global date range filter
  const DATE_RANGES = [
    { label: 'Last 30 Days', days: 30 },
    { label: '1 Month',      days: 30 },
    { label: '3 Months',     days: 90 },
    { label: '6 Months',     days: 180 },
    { label: '1 Year',       days: 365 },
  ];
  const [dateRangeDays, setDateRangeDays] = useState(30);
  const [dateRangeLabel, setDateRangeLabel] = useState('Last 30 Days');
  const [showDateDropdown, setShowDateDropdown] = useState(false);

  useEffect(() => { loadDashboard(); }, [dateRangeDays]); // eslint-disable-line

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get(`/api/v1/dashboard/stats?days=${dateRangeDays}`);
      const data = resp.data as any;
      const kpis = data.kpis;
      const exec = data.execution_summary;
      setStats({
        totalTestCases: kpis.total_test_cases,
        totalScenarios: kpis.total_scenarios,
        totalProjects: kpis.total_projects,
        totalRuns: kpis.total_runs,
        completedRuns: kpis.completed_runs,
        passCount: exec.pass_count,
        failCount: exec.fail_count,
        blockedCount: exec.blocked_count,
        successRate: exec.success_rate,
        executionTrend: data.execution_trend || [],
      });
      setRecentRuns(data.recent_runs || []);
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityReport = async () => {
    setActLoading(true);
    try {
      const resp = await apiClient.get(
        `/api/v1/dashboard/activity-report?period=${actPeriod}&view=${actView}&months_back=${actMonths}`
      );
      const data = resp.data as any;
      setActRows(data.rows || []);
      setActUsers(data.users || []);
      setActUserFilter('');
    } catch (e) {
      console.error('Activity report error', e);
      setActRows([]);
      setActUsers([]);
    } finally {
      setActLoading(false);
    }
  };

  // Call loadActivityReport on mount and when dependencies change
  useEffect(() => {
    loadActivityReport();
  }, [actPeriod, actView, actMonths]);

  const handleExport = async () => {
    if (!isAdmin) return;
    setExportLoading(true);
    try {
      const authTokens = localStorage.getItem('auth_tokens');
      const tokens = authTokens ? JSON.parse(authTokens) : null;
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(
        `${apiBase}/api/v1/dashboard/export-report?period=${actPeriod}&view=${actView}&months_back=${actMonths}`,
        { headers: { Authorization: `Bearer ${tokens?.access_token}` } }
      );
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assurex_report_${actPeriod}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export error', e);
    } finally {
      setExportLoading(false);
    }
  };

  const formatDuration = (run: any) => {
    if (!run.duration_seconds) return '—';
    const m = Math.floor(run.duration_seconds / 60);
    const s = run.duration_seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d > 1 ? 's' : ''} ago`;
  };

  const statusBadge = (status: string, pct?: number) => {
    if (status === 'COMPLETED') return <span className="run-badge badge-completed">COMPLETED</span>;
    if (status === 'IN_PROGRESS') return <span className="run-badge badge-inprogress">IN PROGRESS</span>;
    if (status === 'ABORTED') return <span className="run-badge badge-failed" style={{ color: pct && pct < 80 ? '#dc2626' : undefined }}>FAILED</span>;
    return <span className="run-badge badge-notstarted">NOT STARTED</span>;
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="dashboard-page">
      {/* Page header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">AssureX Analytics</h1>
          <p className="dash-subtitle">Enterprise QA Performance &amp; Diagnostic Dashboard</p>
        </div>
        <div className="dash-header-actions">
          {/* Date range dropdown */}
          <div className="date-range-wrap" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowDateDropdown(false); }}>
            <button className="dash-btn-outline date-range-btn" onClick={() => setShowDateDropdown(v => !v)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {dateRangeLabel}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {showDateDropdown && (
              <div className="date-range-dropdown">
                {DATE_RANGES.map(opt => (
                  <button key={opt.label} className={`date-range-option${dateRangeLabel === opt.label ? ' selected' : ''}`}
                    onClick={() => { setDateRangeDays(opt.days); setDateRangeLabel(opt.label); setShowDateDropdown(false); }}>
                    {dateRangeLabel === opt.label && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className={`dash-btn-primary${!isAdmin ? ' dash-btn-disabled' : ''}`}
            onClick={handleExport} disabled={!isAdmin || exportLoading}
            title={isAdmin ? 'Export report as CSV' : 'Only Admin users can export reports'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exportLoading ? 'Exporting...' : 'Export Data Report'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="dash-loading">Loading dashboard...</div>
      ) : (
        <>
          {/* KPI Cards — order: Total Test Cases, Failed Executions, Total Projects, Total Runs */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-up">+12%</span>
              </div>
              <div className="kpi-label">Total Test Cases</div>
              <div className="kpi-value">{stats.totalTestCases.toLocaleString()}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-label">Total Test Scenarios</div>
              <div className="kpi-value">{stats.totalScenarios.toLocaleString()}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-purple">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-neutral">STABLE</span>
              </div>
              <div className="kpi-label">Total Projects</div>
              <div className="kpi-value">{stats.totalProjects}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-teal">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-up">+{stats.totalRuns}</span>
              </div>
              <div className="kpi-label">Total Runs</div>
              <div className="kpi-value">{stats.totalRuns}</div>
            </div>
          </div>

          {/* Middle row: Success Rate stat card + Execution Trend chart */}
          <div className="dash-mid-row">

            {/* Overall Success Rate — stat card style (reference design) */}
            <div className="dash-card dash-stat-card">
              <div className="dash-stat-eyebrow">OVERALL SUCCESS RATE</div>
              <div className="dash-stat-big">
                {stats.successRate > 0 ? `${stats.successRate}%` : '—'}
              </div>
              <div className="dash-stat-sub">Test execution pass rate</div>

              {/* Thin segmented progress bar */}
              {(() => {
                const total = stats.passCount + stats.failCount + stats.blockedCount;
                const passPct  = total > 0 ? (stats.passCount  / total) * 100 : 0;
                const failPct  = total > 0 ? (stats.failCount  / total) * 100 : 0;
                const blockPct = total > 0 ? (stats.blockedCount / total) * 100 : 0;
                return (
                  <div className="dash-stat-progress-wrap">
                    <div className="dash-stat-progress-bar">
                      <div style={{ width: `${passPct}%`,  background: '#22c55e' }} />
                      <div style={{ width: `${blockPct}%`, background: '#f59e0b' }} />
                      <div style={{ width: `${failPct}%`,  background: '#ef4444' }} />
                    </div>
                    <div className="dash-stat-progress-labels">
                      <span style={{ color: '#22c55e' }}>● Pass {Math.round(passPct)}%</span>
                      <span style={{ color: '#f59e0b' }}>● Blocked {Math.round(blockPct)}%</span>
                      <span style={{ color: '#ef4444' }}>● Fail {Math.round(failPct)}%</span>
                    </div>
                  </div>
                );
              })()}

              {/* Bottom stats row */}
              <div className="dash-stat-row">
                <div className="dash-stat-item">
                  <span className="dash-stat-num" style={{ color: '#22c55e' }}>{stats.passCount.toLocaleString()}</span>
                  <span className="dash-stat-lbl">Passed</span>
                </div>
                <div className="dash-stat-divider" />
                <div className="dash-stat-item">
                  <span className="dash-stat-num" style={{ color: '#f59e0b' }}>{stats.blockedCount.toLocaleString()}</span>
                  <span className="dash-stat-lbl">Blocked</span>
                </div>
                <div className="dash-stat-divider" />
                <div className="dash-stat-item">
                  <span className="dash-stat-num" style={{ color: '#ef4444' }}>{stats.failCount.toLocaleString()}</span>
                  <span className="dash-stat-lbl">Failed</span>
                </div>
              </div>

              {/* Completed vs pending — removed per user request */}
            </div>

            {/* Test Execution Trend — smooth area chart (reference style) */}
            <div className="dash-card dash-card-wide">
              <div className="dash-card-header">
                <div>
                  <h3>Test Execution Summary</h3>
                  <p>Daily execution count — {(() => {
                    const t = new Date();
                    const s = new Date(t); s.setDate(t.getDate() - 30);
                    const f = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    return `${f(s)} – ${f(t)}, ${t.getFullYear()}`;
                  })()}</p>
                </div>
                <div className="chart-legend">
                  <span className="legend-dot" style={{ background: '#22c55e' }}/> PASSED
                  <span className="legend-dot" style={{ background: '#f59e0b', marginLeft: 10 }}/> BLOCKED
                  <span className="legend-dot" style={{ background: '#ef4444', marginLeft: 10 }}/> FAILED
                </div>
              </div>

              {/* SVG smooth area/line chart */}
              {(() => {
                type TrendPoint = { date: string; pass: number; fail: number; blocked: number };
                // Ensure we always have some shape — fill gaps with zeros
                const rawTrend = (stats as any).executionTrend as TrendPoint[] | undefined;
                const trend: TrendPoint[] = rawTrend && rawTrend.length > 1 ? rawTrend : [];

                if (trend.length < 2) {
                  return (
                    <div className="exec-empty">
                      No execution data yet.{' '}
                      <Link to="/test-runs/create">Create a test run</Link> to see the trend here.
                    </div>
                  );
                }

                const W = 800; const H = 160; const PL = 4; const PR = 4; const PT = 24; const PB = 8;
                const chartW = W - PL - PR;
                const chartH = H - PT - PB;
                const n = trend.length;

                // Add 10% headroom so peak doesn't clip
                const rawMax = Math.max(1, ...trend.map(d => d.pass + d.fail + d.blocked));
                const maxVal = rawMax * 1.10;

                const x = (i: number) => PL + (i / (n - 1)) * chartW;
                const y = (v: number) => PT + chartH - (v / maxVal) * chartH;

                const smooth = (pts: [number, number][]) => {
                  if (pts.length < 2) return '';
                  let d = `M ${pts[0][0]} ${pts[0][1]}`;
                  for (let i = 1; i < pts.length; i++) {
                    const cpX = (pts[i-1][0] + pts[i][0]) / 2;
                    d += ` C ${cpX} ${pts[i-1][1]}, ${cpX} ${pts[i][1]}, ${pts[i][0]} ${pts[i][1]}`;
                  }
                  return d;
                };

                const areaPath = (pts: [number, number][]) => {
                  const linePart = smooth(pts);
                  if (!linePart) return '';
                  const base = PT + chartH;
                  return `${linePart} L ${pts[pts.length-1][0]} ${base} L ${pts[0][0]} ${base} Z`;
                };

                const passLine  = trend.map((d, i) => [x(i), y(d.pass)]     as [number,number]);
                const failLine  = trend.map((d, i) => [x(i), y(d.fail)]     as [number,number]);
                const blockLine = trend.map((d, i) => [x(i), y(d.blocked)]  as [number,number]);

                const fmtDate = (s: string) =>
                  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                // Fixed x-axis labels: 30 days ago, 15 days ago, today
                const today = new Date();
                const d30   = new Date(today); d30.setDate(today.getDate() - 30);
                const d15   = new Date(today); d15.setDate(today.getDate() - 15);
                const fmt   = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const xAxisLabels = [fmt(d30), fmt(d15), fmt(today)];

                // Date range for subtitle
                const rangeLabel = `${fmt(d30)} – ${fmt(today)}, ${today.getFullYear()}`;

                // Find closest data point to mouse X position (in SVG coords)
                const findIdx = (svgX: number, svgWidth: number): number => {
                  const frac = (svgX - PL * (svgWidth / W)) / (chartW * (svgWidth / W));
                  return Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1))));
                };

                // Render chart with React state for hover
                const ChartWithHover: React.FC = () => {
                  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);
                  const svgRef = React.useRef<SVGSVGElement>(null);

                  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    const mouseX = e.clientX - rect.left;
                    setHoverIdx(findIdx(mouseX, rect.width));
                  };

                  const hp = hoverIdx !== null ? trend[hoverIdx] : null;
                  const hx = hoverIdx !== null ? x(hoverIdx) : 0;

                  return (
                    <div className="exec-trend-wrap">
                      <svg ref={svgRef} width="100%" height="160"
                        viewBox={`0 0 ${W} ${H}`}
                        className="exec-trend-svg"
                        preserveAspectRatio="none"
                        style={{ display: 'block', cursor: 'crosshair' }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() => setHoverIdx(null)}
                      >
                        <defs>
                          <linearGradient id="grad-pass" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.20"/>
                            <stop offset="100%" stopColor="#22c55e" stopOpacity="0"/>
                          </linearGradient>
                          <linearGradient id="grad-fail" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.15"/>
                            <stop offset="100%" stopColor="#ef4444" stopOpacity="0"/>
                          </linearGradient>
                          <linearGradient id="grad-block" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.15"/>
                            <stop offset="100%" stopColor="#f59e0b" stopOpacity="0"/>
                          </linearGradient>
                          <clipPath id="chart-clip">
                            <rect x={PL} y={0} width={chartW} height={H} />
                          </clipPath>
                        </defs>

                        {/* Grid lines */}
                        {[0.25, 0.5, 0.75, 1].map(frac => (
                          <line key={frac}
                            x1={PL} x2={PL + chartW}
                            y1={PT + chartH * (1 - frac)} y2={PT + chartH * (1 - frac)}
                            stroke="#f3f4f6" strokeWidth="1"
                          />
                        ))}

                        {/* Area + lines */}
                        <g clipPath="url(#chart-clip)">
                          <path d={areaPath(passLine)}  fill="url(#grad-pass)"  />
                          <path d={areaPath(failLine)}  fill="url(#grad-fail)"  />
                          <path d={areaPath(blockLine)} fill="url(#grad-block)" />
                          <path d={smooth(passLine)}  fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round"/>
                          <path d={smooth(failLine)}  fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
                          <path d={smooth(blockLine)} fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round"/>
                        </g>

                        {/* Hover crosshair */}
                        {hp && (
                          <>
                            <line
                              x1={hx} x2={hx} y1={PT} y2={PT + chartH}
                              stroke="#9ca3af" strokeWidth="1" strokeDasharray="4 3"
                            />
                            {/* Dots on each line */}
                            <circle cx={hx} cy={y(hp.pass)}    r="5" fill="#22c55e" stroke="white" strokeWidth="2"/>
                            <circle cx={hx} cy={y(hp.fail)}    r="5" fill="#ef4444" stroke="white" strokeWidth="2"/>
                            <circle cx={hx} cy={y(hp.blocked)} r="5" fill="#f59e0b" stroke="white" strokeWidth="2"/>

                            {/* Tooltip box — flip left if near right edge */}
                            {(() => {
                              const TW = 150; const TH = 80;
                              const tx = hx + 12 > W - TW ? hx - TW - 12 : hx + 12;
                              const ty = Math.max(PT, Math.min(PT + chartH - TH, y(hp.pass) - 20));
                              return (
                                <g>
                                  <rect x={tx} y={ty} width={TW} height={TH}
                                    rx="6" fill="white" stroke="#e5e7eb" strokeWidth="1"
                                    style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))' }}
                                  />
                                  <text x={tx + 10} y={ty + 16} fontSize="9" fill="#6b7280" fontFamily="Inter,sans-serif" fontWeight="600">
                                    {fmtDate(hp.date)}
                                  </text>
                                  <circle cx={tx + 10} cy={ty + 29} r="4" fill="#22c55e"/>
                                  <text x={tx + 18} y={ty + 33} fontSize="10" fill="#374151" fontFamily="Inter,sans-serif">
                                    Pass: <tspan fontWeight="700" fill="#22c55e">{hp.pass}</tspan>
                                  </text>
                                  <circle cx={tx + 10} cy={ty + 48} r="4" fill="#f59e0b"/>
                                  <text x={tx + 18} y={ty + 52} fontSize="10" fill="#374151" fontFamily="Inter,sans-serif">
                                    Blocked: <tspan fontWeight="700" fill="#f59e0b">{hp.blocked}</tspan>
                                  </text>
                                  <circle cx={tx + 10} cy={ty + 67} r="4" fill="#ef4444"/>
                                  <text x={tx + 18} y={ty + 71} fontSize="10" fill="#374151" fontFamily="Inter,sans-serif">
                                    Fail: <tspan fontWeight="700" fill="#ef4444">{hp.fail}</tspan>
                                  </text>
                                </g>
                              );
                            })()}
                          </>
                        )}
                      </svg>

                      {/* X-axis labels as HTML */}
                      <div className="exec-trend-xaxis">
                        <span>{xAxisLabels[0]}</span>
                        <span>{xAxisLabels[1]}</span>
                        <span>{xAxisLabels[2]}</span>
                      </div>
                    </div>
                  );
                };

                return <ChartWithHover />;
              })()}

              <div className="exec-totals">
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.totalRuns}</span>
                  <span className="exec-total-lbl">Total Runs</span>
                </div>
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.completedRuns}</span>
                  <span className="exec-total-lbl">Completed</span>
                </div>
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.totalRuns - stats.completedRuns}</span>
                  <span className="exec-total-lbl">In Progress / Pending</span>
                </div>
              </div>
            </div>
          </div>

          {/* Historical Activity Report */}
          <div className="dash-card" style={{ marginBottom: '1.25rem' }}>
            <div className="dash-card-header">
              <div>
                <h3>Historical Activity Report</h3>
                <p>Long-term test execution velocity and trends</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['monthly', 'overall', '12 months'] as const).map((opt, i) => (
                  <button key={i}
                    className={`dash-btn-outline`}
                    style={{ padding: '4px 12px', fontSize: '0.75rem', minWidth: 'unset',
                      background: (opt === 'monthly' && actPeriod === 'monthly') || (opt === 'overall' && actView === 'overall') ? '#0f2d6b' : 'white',
                      color: (opt === 'monthly' && actPeriod === 'monthly') || (opt === 'overall' && actView === 'overall') ? 'white' : '#374151',
                      border: '1.5px solid #e5e7eb' }}
                    onClick={() => {
                      if (opt === 'monthly') setActPeriod('monthly');
                      else if (opt === 'overall') setActView('overall');
                      else setActMonths(12);
                    }}>
                    {opt === 'monthly' ? 'Monthly' : opt === 'overall' ? 'Overall' : '12 months'}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {/* Controls + table */}
              <div style={{ flex: 1 }}>
                <div className="act-controls">
                  <div className="act-control-group">
                    <label>Period</label>
                    <select value={actPeriod} onChange={e => setActPeriod(e.target.value as any)} className="act-select">
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="act-control-group">
                    <label>View</label>
                    <select value={actView} onChange={e => setActView(e.target.value as any)} className="act-select">
                      <option value="overall">Overall</option>
                      <option value="by_user">By User</option>
                      <option value="ai_vs_manual">AI vs Manual</option>
                    </select>
                  </div>
                  <div className="act-control-group">
                    <label>Range</label>
                    <select value={actMonths} onChange={e => setActMonths(Number(e.target.value))} className="act-select">
                      <option value={3}>3 months</option>
                      <option value={6}>6 months</option>
                      <option value={12}>12 months</option>
                      <option value={24}>24 months</option>
                    </select>
                  </div>
                  {(actView === 'by_user' || actView === 'ai_vs_manual') && actUsers.length > 0 && (
                    <div className="act-control-group">
                      <label>User</label>
                      <select value={actUserFilter} onChange={e => setActUserFilter(e.target.value)} className="act-select">
                        <option value="">All</option>
                        {actUsers.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {actLoading ? (
                  <div className="act-loading">Loading...</div>
                ) : actRows.length === 0 ? (
                  <div className="act-empty">No activity data for this period.</div>
                ) : (
                  <div className="act-table-wrap">
                    <table className="act-table">
                      <thead>
                        <tr>
                          <th>Period</th>
                          {(actView === 'by_user' || actView === 'ai_vs_manual') && <th>User</th>}
                          {actView === 'ai_vs_manual' ? (
                            <>
                              <th>TS (AI)</th>
                              <th>TS AI %</th>
                              <th>TS (Manual)</th>
                              <th>TS Manual %</th>
                            </>
                          ) : (
                            <>
                              <th>TC</th>
                              <th>TC Executed</th>
                              <th>TS</th>
                              <th>TS Executed</th>
                              <th>Runs Created</th>
                              <th>Runs Completed</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filtered = actRows.filter(r => !actUserFilter || r.user === actUserFilter);

                          if (actView !== 'ai_vs_manual') {
                            // Non-AI view — render as-is
                            return filtered.map((row, i) => (
                              <tr key={i}>
                                <td className="act-period">{row.period}</td>
                                {actView === 'by_user' && <td className="act-user">{row.user}</td>}
                                <td><span className="act-num act-tc">{row.tc_created ?? 0}</span></td>
                                <td><span className="act-num act-tc">{row.tc_executed ?? 0}</span></td>
                                <td><span className="act-num act-sc">{row.sc_created ?? 0}</span></td>
                                <td><span className="act-num act-sc">{row.sc_executed ?? 0}</span></td>
                                <td><span className="act-num act-run">{row.runs_created ?? 0}</span></td>
                                <td><span className="act-num act-run">{row.runs_completed ?? 0}</span></td>
                              </tr>
                            ));
                          }

                          // AI vs Manual — group by period, inject summary row per period
                          const periodOrder: string[] = [];
                          const byPeriod: Record<string, typeof filtered> = {};
                          filtered.forEach(r => {
                            if (!byPeriod[r.sort_key]) {
                              byPeriod[r.sort_key] = [];
                              periodOrder.push(r.sort_key);
                            }
                            byPeriod[r.sort_key].push(r);
                          });

                          const rows: React.ReactNode[] = [];
                          periodOrder.forEach(sk => {
                            const group = byPeriod[sk];
                            const totalAi = group.reduce((s, r) => s + (r.sc_ai ?? 0), 0);
                            const totalManual = group.reduce((s, r) => s + (r.sc_manual ?? 0), 0);
                            const total = totalAi + totalManual;
                            const aiPct = total > 0 ? Math.round((totalAi / total) * 1000) / 10 : 0;
                            const manualPct = total > 0 ? Math.round((totalManual / total) * 1000) / 10 : 0;
                            const periodLabel = group[0].period;

                            // Summary row
                            rows.push(
                              <tr key={`${sk}-summary`} className="act-row-summary">
                                <td className="act-period">{periodLabel}</td>
                                <td className="act-user act-summary-label">Overall</td>
                                <td><span className="act-num act-tc act-summary-num">{totalAi}</span></td>
                                <td><span className="act-pct-badge act-pct-ai act-summary-pct">{aiPct}%</span></td>
                                <td><span className="act-num act-sc act-summary-num">{totalManual}</span></td>
                                <td><span className="act-pct-badge act-pct-manual act-summary-pct">{manualPct}%</span></td>
                              </tr>
                            );

                            // Individual user rows
                            group.forEach((row, i) => {
                              rows.push(
                                <tr key={`${sk}-${i}`}>
                                  <td className="act-period act-period-indent">{row.period}</td>
                                  <td className="act-user">{row.user}</td>
                                  <td><span className="act-num act-tc">{row.sc_ai ?? 0}</span></td>
                                  <td><span className="act-pct-badge act-pct-ai">{row.sc_ai_pct ?? 0}%</span></td>
                                  <td><span className="act-num act-sc">{row.sc_manual ?? 0}</span></td>
                                  <td><span className="act-pct-badge act-pct-manual">{row.sc_manual_pct ?? 0}%</span></td>
                                </tr>
                              );
                            });
                          });
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Download CSV */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minWidth: 180 }}>
                <button className={`dash-btn-primary${!isAdmin ? ' dash-btn-disabled' : ''}`}
                  onClick={handleExport} disabled={!isAdmin || exportLoading}
                  style={{ width: '100%', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download CSV Full Report
                </button>
              </div>
            </div>
          </div>

          {/* Bottom row: Recent Test Runs + Quick Stats */}
          <div className="dash-bottom-row">
            <div className="dash-card dash-card-wide">
              <div className="dash-card-header">
                <div>
                  <h3>Recent Test Runs</h3>
                </div>
                <Link to="/test-runs" className="dash-view-all">View All Activity Reports</Link>
              </div>
              {recentRuns.length === 0 ? (
                <div className="exec-empty">No test runs yet. <Link to="/test-runs/create">Create your first test run</Link>.</div>
              ) : (
                <table className="runs-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Test Run Name</th>
                      <th>Status</th>
                      <th>Success Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map(run => (
                      <tr key={run.id}>
                        <td className="run-duration">{new Date(run.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td>
                          <Link to={`/test-runs/${run.id}`} className="run-name">{run.name}</Link>
                        </td>
                        <td>{statusBadge(run.status, run.pass_percentage)}</td>
                        <td>
                          <span className={run.pass_percentage < 80 ? 'run-rate-fail' : 'run-rate-pass'}>
                            {run.pass_percentage != null ? `${Math.round(run.pass_percentage * 10) / 10}%` : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="dash-card">
              <div className="dash-card-header"><h3>Quick Stats</h3></div>
              <div className="quick-stats">
                {[
                  { label: 'Projects', value: stats.totalProjects, icon: '📁', link: '/' },
                  { label: 'Test Cases', value: stats.totalTestCases, icon: '✅', link: '/test-cases' },
                  { label: 'Test Scenarios', value: stats.totalScenarios, icon: '🔬', link: '/test-scenarios' },
                  { label: 'Test Runs', value: stats.totalRuns, icon: '▶️', link: '/test-runs' },
                ].map(item => (
                  <Link key={item.label} to={item.link} className="quick-stat-item">
                    <span className="quick-stat-icon">{item.icon}</span>
                    <div className="quick-stat-info">
                      <span className="quick-stat-value">{item.value.toLocaleString()}</span>
                      <span className="quick-stat-label">{item.label}</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

};
